import { useEffect, useState } from "react";
import { api, formatARS } from "@/lib/api";
import { useOrg } from "@/pages/DashboardLayout";
import { PublicLinkBanner } from "@/components/PublicLink";
import { TrendingUp, Calendar, Clock, CheckCircle2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function Overview() {
  const { org } = useOrg();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/stats");
      setStats(data);
    })();
  }, []);

  if (!stats) return <div className="text-zinc-500 font-heading">CARGANDO...</div>;

  const cards = [
    { icon: TrendingUp, label: "Ingresos totales", value: formatARS(stats.total_income), color: "emerald" },
    { icon: Clock, label: "Turnos pendientes", value: stats.bookings_pending, color: "amber" },
    { icon: CheckCircle2, label: "Turnos confirmados", value: stats.bookings_confirmed, color: "emerald" },
    { icon: Calendar, label: "Turnos de hoy", value: stats.today_bookings, color: "blue" },
  ];

  return (
    <div>
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-2">// Bienvenido</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight break-words">{org?.name?.toUpperCase()}</h1>
      </div>

      <PublicLinkBanner org={org} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={i} className="bg-[#141414] border border-[#262626] rounded-lg p-6 hover:border-emerald-500/50 transition" data-testid={`stat-card-${i}`}>
              <Icon className="h-5 w-5 text-emerald-400 mb-4" strokeWidth={1.5} />
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2">{c.label}</div>
              <div className="font-heading text-4xl">{c.value}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-[#141414] border border-[#262626] rounded-lg p-6 lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-6">// Ingresos últimos 7 días</div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={stats.daily_income}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
              <XAxis dataKey="date" stroke="#71717a" tick={{ fontSize: 11 }} />
              <YAxis stroke="#71717a" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", borderRadius: 6 }} formatter={(v) => formatARS(v)} />
              <Line type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} dot={{ fill: "#10B981", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-lg p-6">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-6">// Por deporte</div>
          <div className="space-y-5">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm uppercase tracking-widest text-zinc-400">Fútbol</span>
                <span className="font-heading text-xl text-emerald-400">{formatARS(stats.by_sport?.futbol)}</span>
              </div>
              <div className="h-2 bg-[#262626] rounded">
                <div
                  className="h-full bg-emerald-500 rounded"
                  style={{ width: `${stats.total_income ? (stats.by_sport?.futbol / stats.total_income) * 100 : 0}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm uppercase tracking-widest text-zinc-400">Pádel</span>
                <span className="font-heading text-xl text-amber-400">{formatARS(stats.by_sport?.padel)}</span>
              </div>
              <div className="h-2 bg-[#262626] rounded">
                <div
                  className="h-full bg-amber-500 rounded"
                  style={{ width: `${stats.total_income ? (stats.by_sport?.padel / stats.total_income) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-[#262626]">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2">Tu link público</div>
            <a
              href={`/book/${org?.slug}`}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-emerald-400 hover:text-emerald-300 break-all"
              data-testid="public-link"
            >
              {window.location.origin}/book/{org?.slug}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
